/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `PlusProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface PlusProps {
    /**
     * A string representing the CSS class to be applied to the PlusIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#787D81'
     */
    color1?: string;
    /**
     * A number or string representing the height of the PlusIcon element.
     *
     * @default 14
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Plus'
     */
    testId?: string;
    /**
     * A number or string representing the height of the PlusIcon element.
     *
     * @default 14
     */
    width?: number | string;
}

/**
 * The `Plus` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `PlusProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the PlusIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the PlusIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the PlusIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const PlusComponent = <Plus className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Plus = ({
    className = '',
    color1 = '#787D81',
    height = 14,
    ref,
    testId = 'Plus',
    width = 14
}: PlusProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 14 14"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M7.63 1.67c-.03-.44-.3-.8-.65-.8s-.64.4-.64.86l.02 4.66-4.62-.05H1.7c-.45.02-.8.28-.81.6-.01.34.37.62.84.62l4.64.04.01 4.67v.06c.03.44.3.8.65.8s.64-.4.64-.86L7.64 7.6l4.62.05h.05c.45-.02.8-.28.81-.6.01-.34-.37-.62-.84-.62L7.64 6.4l-.01-4.67z" fill={color1} fillRule="evenodd" />
    </svg>
);

Plus.displayName = 'Plus';